import { View, Text, Pressable } from 'react-native';
import { useRouter, type Href } from 'expo-router';
import Animated, { FadeInDown } from 'react-native-reanimated';
import { CheckCircle, Circle, ChevronRight, AlertCircle } from 'lucide-react-native';
import { useThemeColors } from '@/lib/useThemeColors';
import { useProfileStore } from '@/lib/state/profile-store';
import { OnboardingStep, STARTUP_OPTIONAL_STEPS, INVESTOR_OPTIONAL_STEPS } from '@/lib/state/inverio-store';
import AnimatedPressable from '@/components/AnimatedPressable';

interface ProfileCompletenessProps {
  showDetails?: boolean;
  compact?: boolean;
}

// Human-readable labels for each step
const STEP_LABELS: Record<OnboardingStep, string> = {
  welcome: 'Welcome',
  'profile-method': 'Profile Method',
  'profile-basic': 'Basic Profile',
  'profile-details': 'Questionnaire',
  'data-room': 'Data Room',
  fundraising: 'Fundraising Status',
  linkedin: 'LinkedIn Verification',
  'investment-history': 'Investment History',
  portfolio: 'Portfolio Companies',
  complete: 'Complete',
};

// Routes to complete each step
const STEP_ROUTES: Partial<Record<OnboardingStep, string>> = {
  'profile-details': '/(onboarding)/questionnaire-problem',
  'data-room': '/(onboarding)/data-room-connect',
  fundraising: '/(startup)/company', // Access via profile
  'investment-history': '/(onboarding)/investment-history',
  portfolio: '/(onboarding)/portfolio-companies',
};

export function ProfileCompleteness({ showDetails = true, compact = false }: ProfileCompletenessProps) {
  const colors = useThemeColors();
  const router = useRouter();
  const userRole = useProfileStore((s) => s.userRole);
  const startupOnboarding = useProfileStore((s) => s.startupOnboarding);
  const investorOnboarding = useProfileStore((s) => s.investorOnboarding);
  const getProfileCompleteness = useProfileStore((s) => s.getProfileCompleteness);

  const completeness = getProfileCompleteness();
  const onboarding = userRole === 'startup' ? startupOnboarding : investorOnboarding;
  const optionalSteps = userRole === 'startup' ? STARTUP_OPTIONAL_STEPS : INVESTOR_OPTIONAL_STEPS;

  if (!onboarding) return null;

  const { completedSteps, skippedSteps } = onboarding;

  // Find incomplete optional steps
  const incompleteSteps = optionalSteps.filter(
    (step) => !completedSteps.includes(step) && !skippedSteps.includes(step)
  );

  // Get skipped steps that can be completed later
  const skippedOptionalSteps = skippedSteps.filter((step) => optionalSteps.includes(step));

  const getProgressColor = () => {
    if (completeness >= 80) return colors.traffic.green;
    if (completeness >= 50) return colors.amber.main;
    return colors.traffic.red;
  };

  const handleCompleteStep = (step: OnboardingStep) => {
    const route = STEP_ROUTES[step];
    if (route) {
      router.push(route as Href);
    }
  };

  if (compact) {
    return (
      <View className="flex-row items-center gap-3">
        <View
          className="w-10 h-10 rounded-full items-center justify-center"
          style={{ backgroundColor: `${getProgressColor()}20` }}
        >
          <Text
            style={{ color: getProgressColor() }}
            className="text-sm font-bold"
          >
            {completeness}%
          </Text>
        </View>
        <View className="flex-1">
          <View
            className="h-2 rounded-full overflow-hidden"
            style={{ backgroundColor: colors.border.light }}
          >
            <View
              className="h-full rounded-full"
              style={{
                backgroundColor: getProgressColor(),
                width: `${completeness}%`,
              }}
            />
          </View>
        </View>
      </View>
    );
  }

  return (
    <Animated.View
      entering={FadeInDown.duration(500)}
      className="rounded-2xl overflow-hidden"
      style={{ backgroundColor: colors.surface.primary }}
    >
      {/* Header */}
      <View className="p-4">
        <View className="flex-row items-center justify-between mb-3">
          <Text style={{ color: colors.text.primary }} className="font-semibold text-lg">
            Profile Completeness
          </Text>
          <Text
            style={{ color: getProgressColor() }}
            className="font-bold text-lg"
          >
            {completeness}%
          </Text>
        </View>

        {/* Progress Bar */}
        <View
          className="h-3 rounded-full overflow-hidden"
          style={{ backgroundColor: colors.border.light }}
        >
          <View
            className="h-full rounded-full"
            style={{
              backgroundColor: getProgressColor(),
              width: `${completeness}%`,
            }}
          />
        </View>

        {completeness < 100 && (
          <Text style={{ color: colors.text.tertiary }} className="text-xs mt-2">
            Complete your profile to increase visibility
          </Text>
        )}
      </View>

      {/* Skipped Steps Section */}
      {showDetails && skippedOptionalSteps.length > 0 && (
        <View
          className="px-4 pb-4 pt-2"
          style={{ borderTopWidth: 1, borderTopColor: colors.border.light }}
        >
          <View className="flex-row items-center gap-2 mb-3">
            <AlertCircle size={16} color={colors.amber.main} />
            <Text style={{ color: colors.text.secondary }} className="text-sm font-medium">
              Skipped Steps ({skippedOptionalSteps.length})
            </Text>
          </View>

          <View className="gap-2">
            {skippedOptionalSteps.map((step) => (
              <AnimatedPressable
                key={step}
                onPress={() => handleCompleteStep(step)}
                className="flex-row items-center p-3 rounded-xl"
                style={{ backgroundColor: colors.surface.secondary }}
              >
                <Circle size={18} color={colors.text.tertiary} />
                <Text
                  style={{ color: colors.text.primary }}
                  className="flex-1 ml-3 text-sm"
                >
                  {STEP_LABELS[step]}
                </Text>
                <Text style={{ color: colors.primary[600] }} className="text-xs mr-1">
                  Complete
                </Text>
                <ChevronRight size={16} color={colors.primary[600]} />
              </AnimatedPressable>
            ))}
          </View>
        </View>
      )}

      {/* Completed Steps Summary */}
      {showDetails && completeness === 100 && (
        <View
          className="px-4 pb-4 pt-2"
          style={{ borderTopWidth: 1, borderTopColor: colors.border.light }}
        >
          <View className="flex-row items-center gap-2">
            <CheckCircle size={20} color={colors.traffic.green} />
            <Text style={{ color: colors.traffic.green }} className="text-sm font-medium">
              Profile Complete!
            </Text>
          </View>
        </View>
      )}
    </Animated.View>
  );
}

/**
 * Small badge showing profile completeness
 */
export function ProfileCompletenessBadge() {
  const colors = useThemeColors();
  const getProfileCompleteness = useProfileStore((s) => s.getProfileCompleteness);
  const completeness = getProfileCompleteness();

  const getColor = () => {
    if (completeness >= 80) return colors.traffic.green;
    if (completeness >= 50) return colors.amber.main;
    return colors.traffic.red;
  };

  return (
    <View
      className="px-2 py-1 rounded-full flex-row items-center gap-1"
      style={{ backgroundColor: `${getColor()}20` }}
    >
      <View
        className="w-2 h-2 rounded-full"
        style={{ backgroundColor: getColor() }}
      />
      <Text style={{ color: getColor() }} className="text-xs font-medium">
        {completeness}%
      </Text>
    </View>
  );
}
